import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "antd";
import ToggleButton from "../togglebutton/ToggleButton";
import Select from "../select/Select";
import { Button1, Button2 } from "../buttons/Buttons";
import Checkbox from "../checkbox/Checkbox";
import { SuccessTag, PendingTag, FailedTag } from "../tag/Tag";

import { DatePicker } from "antd";
import sortIcon from "../../assets/icons/sort-icon.png";
import plusIcon from "../../assets/icons/plus-circle-white.png";
import receiveIcon from "../../assets/icons/receive-money-icon.png";
import sendIcon from "../../assets/icons/send-money-icon.png";
import Table from "../table/Table";
import { sortTransactions, exportToCSV } from "../../data/transactions";
import { useTransactions } from "../../data/live";
import { Select as AntSelect } from "antd";
import CurrencyFlag from "../currency-flag/CurrencyFlag";
import { useAccounts } from "../../data/live";

const statusTag = (status) => {
  if (status === "PENDING")
    return <PendingTag tagName="PENDING" className="rounded-2" />;
  if (status === "FAILED")
    return <FailedTag tagName="FAILED" className="rounded-2" />;
  return <SuccessTag tagName="SUCCEED" className="rounded-2" />;
};

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const endOfDay = (d) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};

const inRange = (txDate, range, customDate) => {
  if (!txDate) return false;
  const t = new Date(txDate);
  const now = new Date();
  if (customDate) {
    return t >= startOfDay(customDate) && t <= endOfDay(customDate);
  }
  if (range === "Today") {
    return t >= startOfDay(now) && t <= endOfDay(now);
  }
  if (range === "This week") {
    const dow = now.getDay() || 7; // Mon=1
    const monday = startOfDay(now);
    monday.setDate(monday.getDate() - (dow - 1));
    return t >= monday && t <= endOfDay(now);
  }
  if (range === "This month") {
    const first = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    return t >= first && t <= endOfDay(now);
  }
  return true;
};


const TransactionHistorySection = () => {
  const navigate = useNavigate();
  const [rangeFilter, setRangeFilter] = useState("Today");
  const [customDate, setCustomDate] = useState(null);
  const [sortBy, setSortBy] = useState("date-desc");
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  // The "Wallet Account" control in the design filters the list by wallet.
  // It was wired to a menu of export actions instead, so the label described
  // one thing and the options did another.
  const [walletFilter, setWalletFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(4);
  // Pull a generous window so client-side filters have data to chew on.
  const { items: liveItems } = useTransactions({ limit: 50 });
  const { accounts } = useAccounts();

  const options = ["Today", "This week", "This month"];

  // Filter by wallet, "All wallets" plus one entry per wallet the user holds.
  const walletFilterOptions = accounts.map((a) => ({
    label: a.currency,
    value: a.id,
    currency: a.currency,
  }));

  const exportOptions = [
    { name: "Export as CSV", value: "export-csv" },
    { name: "Download Statement (PDF)", value: "download-pdf" },
    { name: "Print", value: "print" },
    { name: "Refresh", value: "refresh" },
  ];

  const sortMenu = {
    items: [
      { key: "date-desc", label: "Date, newest first" },
      { key: "date-asc", label: "Date, oldest first" },
      { key: "amount-desc", label: "Amount, high to low" },
      { key: "amount-asc", label: "Amount, low to high" },
      { key: "name-asc", label: "Counterparty, A to Z" },
    ],
    onClick: ({ key }) => setSortBy(key),
  };

  const handleWalletAction = (e) => {
    const value = e.target.value;
    if (value === "export-csv") {
      exportToCSV(searchedTransactions, "recent-transactions.csv");
    } else if (value === "download-pdf") {
      window.print();
    } else if (value === "print") {
      window.print();
    } else if (value === "refresh") {
      window.location.reload();
    }
  };

  const filteredItems = useMemo(() => {
    return liveItems
      .filter((t) => inRange(t.raw?.createdAt || t.date, rangeFilter, customDate))
      .filter((t) => {
        if (!walletFilter) return true;
        const raw = t.raw || {};
        // A transaction belongs to a wallet as either side of the movement.
        return (
          raw.sourceAccountId === walletFilter || raw.destAccountId === walletFilter
        );
      });
  }, [liveItems, rangeFilter, customDate, walletFilter]);

  const sortedTransactions = useMemo(
    () => sortTransactions(filteredItems, sortBy),
    [sortBy, filteredItems]
  );

  const searchedTransactions = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return sortedTransactions;

    return sortedTransactions.filter((t) => {
      const haystack = [
        t.type,
        t.counterparty,
        t.date,
        t.amount,
        t.currency,
        t.status,
        t.direction,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [searchTerm, sortedTransactions]);

  const totalPages = Math.max(1, Math.ceil(searchedTransactions.length / pageSize));

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, rangeFilter, customDate, sortBy, pageSize, walletFilter]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const paginatedTransactions = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return searchedTransactions.slice(start, start + pageSize);
  }, [currentPage, pageSize, searchedTransactions]);

  const firstResult = searchedTransactions.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const lastResult = Math.min(currentPage * pageSize, searchedTransactions.length);

  const allIds = paginatedTransactions.map((t) => t.id);
  const allSelected =
    allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleAll = () => {
    setSelectedIds(allSelected ? [] : allIds);
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectedTransactions = searchedTransactions.filter((t) =>
    selectedIds.includes(t.id)
  );

  const clearSelection = () => setSelectedIds([]);

  const exportSelected = () => {
    if (selectedTransactions.length === 0) return;
    exportToCSV(selectedTransactions, "selected-transactions.csv");
  };

  const transactionTableHeader = [
    <Checkbox
      checked={allSelected}
      indeterminate={someSelected}
      onChange={toggleAll}
    />,
    "Activity",
    "Date and Time",
    "Amount",
    "Status",
    "Action",
  ];

  const transactionTableBody = paginatedTransactions.map((t) => ({
    activityDetails: {
      name: t.type,
      from: t.counterparty,
      icon: t.direction === "in" ? receiveIcon : sendIcon,
      direction: t.direction,
    },
    date: t.date,
    amount: t.amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    currency: t.currency,
    status: statusTag(t.status),
    direction: t.direction,
    selected: selectedIds.includes(t.id),
    onToggle: () => toggleOne(t.id),
    onSendAgain: () =>
      navigate("/new-transfer", {
        state: { prefillRecipient: t.counterparty },
      }),
  }));

  return (
    <div className="">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <div className="d-flex align-items-center gap-2">
          <h5 className="mb-2">Transaction History</h5>
        </div>
        <span
          className="fw-medium cursor-pointer fs14px"
          style={{ color: "#1BBB6B" }}
          onClick={() => navigate("/my-transaction")}
        >
          See all
        </span>
      </div>

      <div className="transaction-history-toolbar">
        <ToggleButton
          options={options}
          defaultValue="Today"
          className="fs14px"
          onChange={(value) => setRangeFilter(value)}
        />

        <div className="transaction-history-filters">
{/* Flags in the list, the way the design shows it. The shared Select
              is a native <select>, whose options can only hold text. */}
          <AntSelect
            value={walletFilter || undefined}
            placeholder="Wallet Account"
            className="transaction-wallet-filter"
            style={{ width: 180, height: 40 }}
            onChange={(value) => setWalletFilter(value || "")}
            allowClear
            options={walletFilterOptions}
            optionRender={(option) => (
              <span className="d-flex align-items-center gap-2">
                {option.data.currency && (
                  <CurrencyFlag currency={option.data.currency} size={20} />
                )}
                <span>{option.data.label}</span>
              </span>
            )}
            labelRender={(label) => (
              <span className="d-flex align-items-center gap-2">
                {label.currency && <CurrencyFlag currency={label.currency} size={18} />}
                <span>{label.label}</span>
              </span>
            )}
          />

          <Select
            defaultValue="Actions"
            className="border rounded-pill"
            selectStyle={{ width: "150px" }}
            options={exportOptions}
            onChange={handleWalletAction}
          />
          <DatePicker
            className="rounded-4"
            placeholder="Custom Date"
            style={{ height: "40px" }}
            onChange={(d) => setCustomDate(d ? d.toDate() : null)}
          />

          <Dropdown menu={sortMenu} trigger={["click"]} placement="bottomRight">
            <div>
              <Button2
                icon={<img src={sortIcon} alt="sort" width={14} />}
                buttonName={"Sort"}
                className={"border px-3 py-2"}
                divClassName={"gap-1 fs14px"}
              />
            </div>
          </Dropdown>

          <Button2
            icon={<img src={plusIcon} alt="add" width={14} />}
            buttonName={"New Transaction"}
            divClassName={"gap-1 fs14px"}
            className={"btn-dark px-3 py-2"}
            onClick={() => navigate("/new-transfer")}
          />
        </div>
      </div>


      <div className="transaction-search-row">
        <div className="transaction-search">
          <input
            type="search"
            value={searchTerm}
            placeholder="Search transactions"
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search transactions"
          />
        </div>
      </div>
      {/* Bulk-action bar, shows only when rows are selected */}
      {selectedIds.length > 0 && (
        <div
          className="d-flex align-items-center justify-content-between mt-2 px-3 py-2 rounded-3"
          style={{ background: "rgba(27, 187, 107, 0.1)", border: "1px solid var(--accent-color)" }}
        >
          <span className="fs14px fw-medium" style={{ color: "var(--accent-color)" }}>
            {selectedIds.length} selected
          </span>
          <div className="d-flex gap-2">
            <Button1
              buttonName="Export Selected"
              className="btn-outline-success"
              onClick={exportSelected}
            />
            <Button1
              buttonName="Clear"
              className="border"
              onClick={clearSelection}
            />
          </div>
        </div>
      )}

      {/* Transaction History Table */}
      <div className="mt-2">
        <Table
          tableBody={transactionTableBody}
          tableHeaders={transactionTableHeader}
          className={"w-100"}
          keyNumber1={1}
          TransactionTable={true}
          condition1={"col-5"}
        />
      </div>

      {searchedTransactions.length === 0 && (
        <div className="transaction-empty-state">
          <img src="/no-data-available.png" alt="No data" className="transaction-empty-svg" />
        </div>
      )}

      <div className="transaction-pagination">
        <span>
          Showing {firstResult}-{lastResult} of {searchedTransactions.length}
        </span>
        <div className="d-flex align-items-center gap-2">
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            aria-label="Rows per page"
          >
            <option value={4}>4 / page</option>
            <option value={8}>8 / page</option>
            <option value={12}>12 / page</option>
          </select>
          <button
            type="button"
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <strong>
            {currentPage} / {totalPages}
          </strong>
          <button
            type="button"
            onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionHistorySection;





